import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlaylistModal } from '../../stores/playlistStore';
import './AddToPlaylistModal.css';

interface AddToPlaylistModalProps {
  courseId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export const AddToPlaylistModal: React.FC<AddToPlaylistModalProps> = ({
  courseId: propCourseId,
  isOpen: propIsOpen,
  onClose: propOnClose,
}) => {
  const { t } = useTranslation(['playlist', 'common']);
  const {
    isOpen: storeIsOpen,
    courseId: storeCourseId,
    playlists,
    loading,
    error,
    close: storeClose,
    addCourse,
    removeCourse,
    createPlaylist,
  } = usePlaylistModal();

  const isOpen = propIsOpen ?? storeIsOpen;
  const courseId = propCourseId ?? storeCourseId;
  const onClose = propOnClose ?? storeClose;

  const [isCreating, setIsCreating] = useState(false);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const handleTogglePlaylist = async (playlistId: string, hasCourse: boolean) => {
    if (!courseId) return;

    try {
      if (hasCourse) {
        await removeCourse(playlistId, courseId);
      } else {
        await addCourse(playlistId, courseId);
      }
    } catch (err) {
      console.error('Failed to toggle playlist:', err);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistTitle.trim()) {
      setCreateError(t('playlist:nameRequired'));
      return;
    }

    try {
      setCreateError(null);
      const newPlaylist = await createPlaylist({ title: newPlaylistTitle.trim() });
      // Auto-add course to new playlist
      if (courseId) {
        await addCourse(newPlaylist.id, courseId);
      }
      setNewPlaylistTitle('');
      setIsCreating(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('playlist:createFailed'));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreatePlaylist();
    } else if (e.key === 'Escape') {
      setIsCreating(false);
      setNewPlaylistTitle('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="playlist-modal-overlay" onClick={onClose}>
      <div className="playlist-modal" onClick={(e) => e.stopPropagation()}>
        <div className="playlist-modal-header">
          <h3>{t('playlist:modal.title')}</h3>
          <button className="playlist-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="playlist-modal-content">
          {error && <div className="playlist-modal-error">{error}</div>}

          {loading && playlists.length === 0 ? (
            <div className="playlist-modal-loading">{t('common:loading')}</div>
          ) : playlists.length === 0 && !isCreating ? (
            <div className="playlist-modal-empty">
              <p>{t('playlist:modal.emptyMessage')}</p>
              <p>{t('playlist:modal.emptyHint')}</p>
            </div>
          ) : (
            <ul className="playlist-list">
              {playlists.map((playlist) => {
                const count = playlist.playlistCourses?.length || 0;
                return (
                  <li key={playlist.id} className="playlist-item">
                    <label className="playlist-checkbox-label">
                      <input
                        type="checkbox"
                        checked={playlist.hasCourse}
                        onChange={() =>
                          handleTogglePlaylist(playlist.id, playlist.hasCourse)
                        }
                        disabled={loading}
                      />
                      <span className="playlist-title">{playlist.title}</span>
                      <span className="playlist-count">
                        {count === 1
                          ? t('playlist:courseCountSingular', { count })
                          : t('playlist:courseCount', { count })}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {isCreating ? (
            <div className="playlist-create-form">
              <input
                type="text"
                placeholder={t('playlist:namePlaceholder')}
                value={newPlaylistTitle}
                onChange={(e) => setNewPlaylistTitle(e.target.value)}
                onKeyDown={handleKeyPress}
                autoFocus
                className="playlist-create-input"
              />
              {createError && (
                <div className="playlist-create-error">{createError}</div>
              )}
              <div className="playlist-create-actions">
                <button
                  className="playlist-btn playlist-btn-cancel"
                  onClick={() => {
                    setIsCreating(false);
                    setNewPlaylistTitle('');
                    setCreateError(null);
                  }}
                >
                  {t('common:cancel')}
                </button>
                <button
                  className="playlist-btn playlist-btn-create"
                  onClick={handleCreatePlaylist}
                  disabled={loading || !newPlaylistTitle.trim()}
                >
                  {t('playlist:create')}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="playlist-add-new-btn"
              onClick={() => setIsCreating(true)}
            >
              {t('playlist:modal.createNew')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddToPlaylistModal;
